import { Trophy } from 'lucide-react';
import type { RankedEntity } from '../types/statistics';
import { formatEuro } from '../utils/currency';

interface ClientRankingsProps {
  clients: RankedEntity[];
  loading?: boolean;
}

export function ClientRankings({ clients, loading = false }: ClientRankingsProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-pulse rounded-2xl bg-gray-100 p-6 h-32"></div>
        ))}
      </div>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Nenhum cliente encontrado</p>
      </div>
    );
  }

  const getTierBadge = (tier?: string) => {
    if (!tier) return null;

    const colors = {
      Platinum: 'bg-gradient-to-r from-slate-300 to-slate-500 text-white',
      Gold: 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white',
      Silver: 'bg-gradient-to-r from-gray-300 to-gray-500 text-white',
      Bronze: 'bg-gradient-to-r from-amber-600 to-amber-800 text-white',
    };

    return (
      <span className={`inline-block px-3 py-1 rounded-lg text-sm font-bold ${colors[tier as keyof typeof colors] || 'bg-gray-300 text-gray-700'}`}>
        {tier}
      </span>
    );
  };

  const topThree = clients.slice(0, 3);
  const rest = clients.slice(3);

  return (
    <div className="space-y-4">
      {topThree.map((client) => (
        <div
          key={client.id}
          className="relative bg-gradient-to-br from-blue-50 to-blue-100/50 border-2 border-blue-400 rounded-2xl p-6 hover:shadow-xl transition-all duration-300"
        >
          <div className="absolute -left-4 top-1/2 -translate-y-1/2 bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl shadow-lg transition-transform hover:scale-[1.02]">
            {client.rank}
          </div>

          <div className="flex items-start justify-between ml-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Trophy className="text-yellow-500" size={24} />
                <h3 className="text-xl font-bold text-gray-900 uppercase">{client.name}</h3>
              </div>

              <div className="flex items-center gap-2 mb-4">
                {getTierBadge(client.tier)}
                <span className="text-gray-600 text-sm">{client.campaign_count} campanhas</span>
              </div>

              <div className="bg-white/60 rounded-xl p-3 inline-block">
                <p className="text-sm text-gray-600 mb-1">Média por campanha: <span className="font-bold text-gray-900">{formatEuro(client.average_value)}</span></p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-4xl font-bold text-blue-600">{formatEuro(client.value)}</p>
              {client.contribution_percentage !== undefined && (
                <p className="text-sm text-gray-600 mt-1">{client.contribution_percentage.toFixed(1)}% do total</p>
              )}
            </div>
          </div>

          {client.contribution_percentage !== undefined && (
            <div className="mt-4 ml-6">
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.min(client.contribution_percentage, 100)}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      ))}

      {rest.map((client) => (
        <div
          key={client.id}
          className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-all duration-300"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="bg-gray-200 text-gray-700 rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                {client.rank}
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-gray-900">{client.name}</h3>
                  {getTierBadge(client.tier)}
                </div>
                <p className="text-sm text-gray-600">
                  {client.campaign_count} campanhas • Média: {formatEuro(client.average_value)}
                </p>
              </div>
            </div>

            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">{formatEuro(client.value)}</p>
              {client.contribution_percentage !== undefined && (
                <p className="text-sm text-gray-600">{client.contribution_percentage.toFixed(1)}% do total</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
